import * as SearchSuggestionClick from '@ori-events/search-suggestion-click';
import { styled, Typography } from '@ori-ui/mui';
import { ContentType, Placement } from '@ori/analytics-data';
import useTestId from '@ori/testid-generator';
import type { VFC } from 'react';
import { useCallback } from 'react';

import { ProductTile } from '~/components/common/ProductTile';
import { DESKTOP_BREAKPOINT } from '~/constants';
import { useAppConfigurationContext } from '~/contexts/AppConfigurationContext';
import { useNavigationContext } from '~/contexts/NavigationContext';
import { formatProduct, getSignalsData } from '~/helpers';
import { useTranslationByKey } from '~/hooks';
import type { Product, SendSignalToFusionProps } from '~/models';
import { TranslationKeys } from '~/models';
import { sendSignalToFusion } from '~/utils';

const PopularProductsRoot = styled('div')(({ theme }) => ({
  boxSizing: 'border-box',
  height: '100%',
  position: 'relative',
  width: '100%',

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    display: 'flex',
    flexDirection: 'column',
    gridColumn: 2,
    gridRow: '1 / 3',
    margin: 0,
    padding: '0 calc(10% - 60px) 0 10%',
    textAlign: 'center',

    '&::before': {
      backgroundColor: theme.palette.grey[100],
      content: '""',
      height: '100%',
      left: 0,
      marginLeft: theme.spacing(5),
      position: 'absolute',
      width: 4,
    },
  },
}));

const PopularProductsHeader = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  marginBottom: theme.spacing(3),

  [theme.breakpoints.up(DESKTOP_BREAKPOINT)]: {
    marginBottom: theme.spacing(-6),
  },
}));

const PopularProductsWrapper = styled('div')(() => ({
  display: 'flex',
  flexWrap: 'wrap',
  margin: 0,
  padding: 0,
}));

interface PopularProductProps {
  formattedToken: string;
}

export const PopularProducts: VFC<PopularProductProps> = ({ formattedToken }) => {
  const { searchResults, searchTimestamp } = useNavigationContext();
  const { tenant, globalApiUrl, currentLanguage } = useAppConfigurationContext();
  const translations = useTranslationByKey();
  const { getTestId } = useTestId();

  const onClick = useCallback(
    (position: number) => {
      if (searchResults?.Products.length == null) {
        return;
      }

      const searchSignalsData = getSignalsData(searchResults, position, searchTimestamp);

      const searchSuggestionClickData: SearchSuggestionClick.Data = {
        itemData: formatProduct(searchResults.Products[position] as Product),
        itemType: ContentType.Product,
        searchQuery: searchResults.Query,
        placement: Placement.TopNavigationSearchSuggestions,
        position,
      };

      const sendSignalData: SendSignalToFusionProps = {
        data: searchSignalsData,
        tenant,
        formattedToken,
        globalApiUrl,
        acceptLanguage: currentLanguage,
      };

      sendSignalToFusion(sendSignalData);

      window.dispatchEvent(
        new CustomEvent<SearchSuggestionClick.Data>(SearchSuggestionClick.Name, {
          detail: searchSuggestionClickData,
        }),
      );
    },
    [searchResults, searchTimestamp, tenant, formattedToken, currentLanguage, globalApiUrl],
  );

  if (!searchResults?.Products) {
    return null;
  }

  return (
    <PopularProductsRoot data-testd={getTestId('popular-products-root')}>
      <PopularProductsHeader>
        {translations(TranslationKeys.PopularProductsInThisSearchQuery)}
      </PopularProductsHeader>
      <PopularProductsWrapper>
        {searchResults.Products.map(
          ({ ProductCode, StandardPrice, ActualPrice, ProductImageUrl, ConceptName }, index) => (
            <ProductTile
              key={ProductCode}
              displayProductName
              largeVersion
              basicCataloguePrice={StandardPrice}
              currentPrice={ActualPrice}
              imageUrl={ProductImageUrl}
              position={index}
              productCode={ProductCode}
              productName={ConceptName}
              onClick={onClick}
            />
          ),
        )}
      </PopularProductsWrapper>
    </PopularProductsRoot>
  );
};
